<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useGlobalStore } from '../../stores/globalStore'
import { useFilingDetailStore } from '../../stores/filingDetailStore'
import { useUserStore } from '../../stores/userStore'
import BModal from '../bootstrap/BModal.vue'
import BToast from '../bootstrap/BToast.vue'

import BeneficialOwnersCard from './BeneficialOwnersCard.vue'
import BusinessAddressCard from './BusinessAddressCard.vue'
import CompanyInfoCard from './CompanyInfoCard.vue'
import CompanyApplicantCard from './CompanyApplicantCard.vue'
import FilingsDetailReopenEdits from './FilingsDetailReopenEdits.vue'

// Initialize the route object
const route = useRoute()

const globalStore = useGlobalStore()
const filingDetailStore = useFilingDetailStore()
const userStore = useUserStore()

const isSubmitting = ref(false)
const showConfirmSubmissionModal = ref(false)
const showIncompleteAlert = ref(false)
const successToastVisible = ref(false)
const isLoading = ref(true)

onMounted(async () => {
  try {
    isLoading.value = true
    await filingDetailStore.getBoiReportData(
      route.params.filingId,
      route.params.teamSlug
    )
  } catch (error) {
    console.error('Error loading filing data:', error)
    // Handle error (e.g., show error message to user)
  } finally {
    isLoading.value = false
  }
})

const showChangeStatusAlert = computed(() => {
  return (
    filingDetailStore.currentFiling.filingStatus === 'submitted_for_processing'
  )
})

function askForConfirmation() {
  if (!filingDetailStore.isReadyForSubmission) {
    showIncompleteAlert.value = true
  } else {
    showConfirmSubmissionModal.value = true
  }
}

async function handleSubmit() {
  isSubmitting.value = true

  try {
    isLoading.value = true
    await filingDetailStore.submitBoiForProcessing()

    // You might want to show a success message or redirect the user
  } catch (error) {
    console.error('Error submitting for processing:', error)
    alert('Failed to submit for processing. Please try again.')
  } finally {
    isLoading.value = false
    isSubmitting.value = false
    successToastVisible.value = true
  }
}

// for success Toast
const onShown = () => {
  console.log('Toast is shown')
}

const onHidden = () => {
  console.log('Toast is hidden')
  successToastVisible.value = false
}
</script>

<template>
  <div v-loading.overlay="isLoading">
    <div>
      <!-- Header -->
      <BToast
        id="exampleToast"
        title="Well Done! Your Filing is on its way!"
        subtitle=""
        body="Your Beneficial Ownership Report has been submitted successfully. We will notify you once we receive a response from FinCEN."
        icon="<i class='fa-duotone fa-solid fa-thumbs-up'></i>"
        variant="info"
        :visible="successToastVisible"
        @shown="onShown"
        @hidden="onHidden"
      />
      <FilingsDetailReopenEdits />
      <div class="card mb-2">
        <div class="card-header border-bottom">
          <div class="row">
            <div class="col-md mb-3">
              <span class="card-subtitle text-primary">{{
                filingDetailStore.currentFiling.typeOfFilingDisplay
              }}</span>
              <h4 class="card-title mb-1">
                {{ filingDetailStore.currentFiling.reportingCompanyLegalName }}
              </h4>
              <div class="mb-1">
                <small class="text-muted fs-4">{{
                  filingDetailStore.currentFiling.reportNameDisplay
                }}</small>
              </div>
              <div>
                <span class="badge bg-soft-success text-success me-2">{{
                  filingDetailStore.currentFiling.filingStatusDisplay
                }}</span>
                <span
                  v-if="filingDetailStore.checkFilingEditable()"
                  class="badge"
                  :class="[
                    'badge',
                    filingDetailStore.isReadyForSubmission
                      ? 'bg-soft-success text-success'
                      : 'bg-soft-danger text-danger'
                  ]"
                  ><i
                    :class="[
                      'fa-light',
                      filingDetailStore.isReadyForSubmission
                        ? 'fa-check-circle'
                        : 'fa-triangle-exclamation'
                    ]"
                  ></i>
                  {{
                    filingDetailStore.isReadyForSubmission
                      ? 'Ready to Submit'
                      : 'Needs Attention - Missing Information'
                  }}</span
                >
              </div>
            </div>
            <div class="col-md-auto mb-3">
              <div class="d-md-flex d-block justify-content-end">
                <button
                  class="btn btn-primary w-100"
                  @click="askForConfirmation"
                  :disabled="
                    isSubmitting || !filingDetailStore.checkFilingEditable()
                  "
                  :class="{
                    'btn-primary': filingDetailStore.checkFilingEditable(),
                    'btn-outline-primary':
                      !filingDetailStore.checkFilingEditable()
                  }"
                >
                  <span
                    v-if="isSubmitting"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <i
                    v-else-if="!filingDetailStore.checkFilingEditable()"
                    class="fas fa-check me-2"
                  ></i>
                  {{
                    isSubmitting
                      ? 'Submitting...'
                      : !filingDetailStore.checkFilingEditable()
                        ? filingDetailStore.currentFiling.filingStatusDisplay
                        : 'Submit for Processing'
                  }}
                  <i
                    class="fa-light fa-chevrons-right"
                    aria-hidden="true"
                    v-if="filingDetailStore.checkFilingEditable()"
                  ></i>
                </button>
              </div>
            </div>
          </div>
          <div v-if="filingDetailStore.checkFilingEditable()" class="row">
            <div class="col-12">
              <div class="alert alert-soft-primary" role="alert">
                <p>
                  Please review the information below and make any necessary
                  changes before submitting your Beneficial Ownership Report for
                  processing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End Header -->
      <CompanyInfoCard />

      <BusinessAddressCard />

      <BeneficialOwnersCard :owners="filingDetailStore.beneficialOwners" />
      <CompanyApplicantCard
        v-if="!filingDetailStore.currentFiling.existingReportingCompany"
        :companyApplicants="filingDetailStore.companyApplicants"
      />
    </div>
    <BModal
      id="confirm-submission"
      title="Confirm Your Submission"
      ok-title="Yes, I agree"
      size="large"
      :visible="showConfirmSubmissionModal"
      @update:visible="showConfirmSubmissionModal = $event"
      @ok="handleSubmit"
    >
      <h5 class="h3">
        We are ready to process your Beneficial Ownership Report
      </h5>
      <p class="lead">
        By confirming, you authorize us to file your Beneficial Ownership Report
        with FinCEN.
      </p>
      <p class="text-muted">
        <strong>Important:</strong> By clicking below, I hereby certify that I
        am an authorized representative, manager, receiver, or trustee empowered
        to file this Beneficial Ownership Information Report (BOIR) on behalf of
        the reporting company. I further certify, on behalf of the reporting
        company, that the information provided with this BOIR is true, accurate,
        and complete. By submitting this information, I affirm under oath that
        it is true and accurate. I understand that knowingly filing false
        information is a violation of federal law and may result in criminal and
        civil penalties. I acknowledge that this filing does not qualify for any
        public records exemptions under applicable law. I recognize that
        Effortless Ventures, LLC is not affiliated with any government or
        governmental entity, including FinCEN, and that I may file directly with
        FinCEN through their official website. I authorize Effortless Ventures,
        LLC or its designated agents to electronically file all submitted
        information with FinCEN on my behalf. I understand that the information
        provided in this application will be filed with FinCEN and that
        submitting false information constitutes a third-degree felony.
        Additionally, by submitting this information, I acknowledge that I have
        read, understood, and agree to be bound by this site's Terms and
        Conditions.
      </p>
      <div
        class="mb-3"
        v-if="userStore.user?.firstName && userStore.user?.lastName"
      >
        <h6 class="fw-bold">Submitted By:</h6>
        <p>{{ userStore.user?.firstName }} {{ userStore.user?.lastName }}</p>
      </div>

      <div class="mb-3" v-if="userStore.user?.email">
        <h6 class="fw-bold">Email Address:</h6>
        <p>{{ userStore.user?.email }}</p>
      </div>
    </BModal>
    <BModal
      id="incomplete-alert"
      title="Incomplete Information"
      ok-title="OK"
      :visible="showIncompleteAlert"
      @update:visible="showIncompleteAlert = $event"
    >
      <div>
        <p>Please complete all required information before submitting:</p>
        <ul>
          <li v-for="item in filingDetailStore.incompleteItems" :key="item">
            {{ item }}
          </li>
        </ul>
      </div>
    </BModal>
  </div>
</template>
